import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

/**
 * ConfirmDialog — Phase 2 reusable component.
 *
 * A generic yes/no confirmation dialog for destructive or important actions
 * (delete candidate, delete user, cancel something, etc.). Purely
 * presentational/controlled — it owns no state and makes no API calls; the
 * calling page remains fully responsible for its own delete/action logic and
 * error handling, exactly as today. This does not change any existing
 * delete/confirm flow yet — pages opt in during their own redesign phase.
 *
 * Props:
 *   open           - boolean, dialog visibility (required, controlled)
 *   title          - dialog title (required)
 *   message        - body text / question (required)
 *   confirmLabel   - confirm button text (default: "Confirm")
 *   cancelLabel    - cancel button text (default: "Cancel")
 *   destructive    - if true, confirm button renders in the error/red color
 *                    (use for delete-type actions)
 *   loading        - if true, disables both buttons (e.g. while an API call
 *                    triggered by onConfirm is in flight) — the calling page
 *                    controls this, not the dialog itself
 *   onConfirm      - called when the confirm button is clicked (required)
 *   onClose        - called when the cancel button or backdrop is clicked
 *                    (required)
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  loading = false,
  onConfirm,
  onClose,
}) {
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 13.5 }}>{message}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          color={destructive ? "error" : "primary"}
          autoFocus
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
